import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export const THEME_STORAGE_KEY = "theme"; // same key as tryon-studio and tryon-web
const DEFAULT_THEME = "light"; // existing users keep the light theme until they choose
const GROUND = { light: "#f8fafc", dark: "#10100f" }; // the page colour, for the browser chrome

const ThemeContext = createContext(undefined);

function readStoredTheme() {
    try {
        const stored = localStorage.getItem(THEME_STORAGE_KEY);
        if (stored === "light" || stored === "dark" || stored === "system") return stored;
    } catch {
        // Storage can be blocked (private windows); fall back to the default.
    }
    return DEFAULT_THEME;
}

function systemPrefersDark() {
    return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolve(theme) {
    return theme === "system" ? (systemPrefersDark() ? "dark" : "light") : theme;
}

/** Light is the absence of data-theme, matching tryon-studio. */
function applyTheme(resolved) {
    const root = document.documentElement;
    if (resolved === "dark") root.setAttribute("data-theme", "dark");
    else root.removeAttribute("data-theme");
    root.style.colorScheme = resolved;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", GROUND[resolved]);
}

/**
 * Holds the chosen theme ("light", "dark" or "system") and keeps <html data-theme> in step.
 * public/index.html applies the saved theme before React loads, so there is no flash.
 */
export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(readStoredTheme);
    const [resolvedTheme, setResolvedTheme] = useState(() => resolve(readStoredTheme()));

    useEffect(() => {
        const next = resolve(theme);
        setResolvedTheme(next);
        applyTheme(next);
        if (theme !== "system") return undefined;

        const query = window.matchMedia("(prefers-color-scheme: dark)");
        const onChange = () => {
            const following = query.matches ? "dark" : "light";
            setResolvedTheme(following);
            applyTheme(following);
        };
        query.addEventListener("change", onChange);
        return () => query.removeEventListener("change", onChange);
    }, [theme]);

    // Another tab changed the theme.
    useEffect(() => {
        const onStorage = (event) => {
            if (event.key === THEME_STORAGE_KEY && ["light", "dark", "system"].includes(event.newValue)) {
                setThemeState(event.newValue);
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    const setTheme = useCallback((next) => {
        setThemeState(next);
        try {
            localStorage.setItem(THEME_STORAGE_KEY, next);
        } catch {
            // The choice still applies for this visit.
        }
    }, []);

    const value = useMemo(() => ({ theme, resolvedTheme, setTheme }), [theme, resolvedTheme, setTheme]);
    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) throw new Error("useTheme must be used inside a ThemeProvider");
    return context;
}
